/**
 * The permission catalogue (decisions-log W9). Roles are sets of these, edited on Settings →
 * «Роли»; the catalogue itself is code. Each screen area has `.read` and `.write`; a few actions
 * stand on their own.
 */
import { z } from 'zod';

export const AREAS = [
  'hours',
  'payplan',
  'invoices',
  'receipts',
  'payouts',
  'clients',
  'contracts',
  'projects',
  'people',
  'entities',
  'documents',
  'expenses',
  'finance',
  'fx',
  'settings',
] as const;
export type Area = (typeof AREAS)[number];

export const AREA_LABELS: Readonly<Record<Area, string>> = {
  hours: 'План проектов',
  payplan: 'План выплат и ставки людей',
  invoices: 'Счета',
  receipts: 'Поступления',
  payouts: 'Выплаты и налоги',
  clients: 'Клиенты',
  contracts: 'Договоры',
  projects: 'Проекты',
  people: 'Команда',
  entities: 'Юрлица, банки и почта',
  documents: 'Библиотека документов',
  expenses: 'Расходы',
  finance: 'Финансы и обзор',
  fx: 'Курсы валют',
  settings: 'Настройки: шаблоны, направления',
};

export const ACTIONS = ['capture.write', 'months.close', 'audit.read', 'data.manage', 'users.manage'] as const;
export type Action = (typeof ACTIONS)[number];

export const ACTION_LABELS: Readonly<Record<Action, string>> = {
  'capture.write': 'Быстрый ввод и исправление пробелов',
  'months.close': 'Закрытие и открытие месяцев',
  'audit.read': 'История изменений',
  'data.manage': 'Экспорт, импорт и резервные копии',
  'users.manage': 'Пользователи и роли',
};

export type AreaPermission = `${Area}.read` | `${Area}.write`;
export type Permission = AreaPermission | Action;

export const PERMISSIONS: readonly Permission[] = [
  ...AREAS.flatMap((area) => [`${area}.read`, `${area}.write`] as const),
  ...ACTIONS,
];

export const permissionSchema = z.enum(PERMISSIONS as [Permission, ...Permission[]]);

/**
 * Permissions that only work with «Все данные» (W10): these screens are built from every
 * entity's records at once, so a person limited to some entities or projects gets 403 there.
 */
export const ALL_DATA_PERMISSIONS: ReadonlySet<Permission> = new Set<Permission>([
  'documents.read',
  'documents.write',
  'expenses.read',
  'expenses.write',
  'finance.read',
  'finance.write',
  'settings.write',
  'audit.read',
  'data.manage',
  'users.manage',
]);

/**
 * Permissions whose screens show what a project bills (W24): «План проектов» its price, «Счета» and
 * «Поступления» its invoices, «Проекты» the price and the invoice history, «Финансы» the revenue.
 * The taxes on a project's revenue on «Выплаты» are for these people only: the tax and its rate
 * give the revenue away.
 */
export const PROJECT_REVENUE_PERMISSIONS: readonly Permission[] = ['hours.read', 'invoices.read', 'receipts.read', 'projects.read', 'finance.read'];

/** True when `granted` (already expanded: write implies read) shows some project's revenue (W24). */
export function canSeeProjectRevenue(granted: Iterable<Permission>): boolean {
  const all = new Set(granted);
  return PROJECT_REVENUE_PERMISSIONS.some((permission) => all.has(permission));
}

/** Write implies read: a role that may change an area may also see it. */
export function expandPermissions(granted: Iterable<Permission>): Set<Permission> {
  const all = new Set<Permission>(granted);
  for (const area of AREAS) if (all.has(`${area}.write`)) all.add(`${area}.read`);
  return all;
}

/** The system role (migration 0003 seeds it and the editable default roles, W9). */
export const OWNER_ROLE_ID = 'role_owner';
export const OWNER_ROLE_NAME = 'Владелец';
